import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { HAL, prng, useIndeling } from './indeling'
import { DAKLICHT_Y, ZON, daklichtRijen } from './daglicht'
import { useAnimatie, useLicht, useObjecten, useProfiel } from '../scene/store'
import { PROFIEL_DETAIL } from '../scene/config'
import { OMLOOP, halklok } from './omloop'

const MAX_RIJEN = 9

const vertex = /* glsl */ `
#define MAX_RIJEN ${MAX_RIJEN}
attribute vec3 snelheid;
uniform float tijd;
uniform float omloop;
uniform float pixels;
uniform vec3 zon;
uniform float dakY;
uniform vec3 rijen[MAX_RIJEN];   // z, x0, x1
uniform int aantalRijen;
uniform float breedte;
varying float vHelder;
varying float vAlfa;

// Een geheel aantal omwentelingen per omloop van de hal (omloop.ts), minstens
// één: dan staat elk stofje na de omloop weer waar het begon.
float rond(float w) {
  return max(1.0, floor(w * omloop / 6.2831853 + 0.5)) * 6.2831853 / omloop;
}

void main() {
  vec3 p = position;
  // Trage, licht chaotische drift — geen rechte lijnen, dat leest als regen.
  p.x += sin(tijd * rond(0.09 * snelheid.x) + position.z * 1.7) * 1.4;
  p.y += sin(tijd * rond(0.06 * snelheid.y) + position.x * 1.3) * 0.7;
  p.z += cos(tijd * rond(0.08 * snelheid.z) + position.y * 2.1) * 1.2;

  // Staat dit deeltje in een zonneschacht? Terug langs de zonnestraal naar het
  // dak, en kijken of dat punt in een daklicht valt. Dezelfde meetkunde als
  // de schachten zelf, dus de glinstering zit precies waar het licht is.
  vec3 dak = p + zon * ((dakY - p.y) / zon.y);
  float inSchacht = 0.0;
  for (int i = 0; i < MAX_RIJEN; i++) {
    if (i >= aantalRijen) break;
    vec3 r = rijen[i];
    float dz = abs(dak.z - r.x);
    float binnen = (1.0 - smoothstep(breedte * 0.3, breedte * 0.5, dz))
                 * step(r.y, dak.x) * step(dak.x, r.z);
    inSchacht = max(inSchacht, binnen);
  }
  vHelder = inSchacht;

  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_Position = projectionMatrix * mv;
  float d = max(-mv.z, 0.25);

  // Onscherp dichtbij: een stofje op een halve meter is een zachte schijf van
  // tientallen pixels, op tien meter een speldenprik. Het licht blijft gelijk,
  // dus hoe groter de schijf, hoe lager de dekking.
  float maat = 1.3 + 60.0 / (d * d);
  // Hooguit 22 px (was 44): grote schijven vlak voor de lens lazen als effect.
  maat = min(maat, 22.0) * pixels;
  gl_PointSize = maat;
  // Dichtbij extra gedempt: een handvol grote schijven vlak voor de lens is
  // sfeer, een wolk ervan is een vuile lens.
  vAlfa = (1.6 / (1.0 + maat * maat * 0.03)) * (1.0 - smoothstep(22.0, 60.0, d)) * smoothstep(0.4, 2.5, d);
}
`

const fragment = /* glsl */ `
uniform vec3 kleurZon;
uniform vec3 kleurHal;
uniform float sterkte;
varying float vHelder;
varying float vAlfa;
void main() {
  vec2 c = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(c, c);
  if (r2 > 1.0) discard;
  // Bokehschijf: vlak met een iets lichtere rand, zoals een lens hem tekent.
  float schijf = smoothstep(1.0, 0.75, r2) * (0.8 + 0.35 * smoothstep(0.35, 0.9, r2));
  vec3 kleur = mix(kleurHal * 0.25, kleurZon * 3.0, vHelder);
  gl_FragColor = vec4(kleur * schijf * vAlfa * sterkte, 1.0);
}
`

/**
 * Stofdeeltjes die traag door de hal dwarrelen.
 *
 * Wat hier stond was een `pointsMaterial` van 0,018 m: vierkante stipjes met
 * overal dezelfde helderheid. Echt stof zie je alleen waar licht op valt, en
 * dan vooral in een zonnestraal — daarbuiten is het onzichtbaar. En dichtbij
 * de lens is het geen stip maar een onscherpe schijf. Die twee dingen samen
 * zijn het beeld dat iedereen van een stoffige hal in de film kent.
 *
 * Een deel van de deeltjes wordt daarom ín de zonneschachten geboren, en de
 * rest over de hal verdeeld. De beweging draait volledig op de GPU: de oude
 * versie schreef elk frame 7.800 getallen vanuit JavaScript.
 */
export function Stof() {
  const ind = useIndeling()
  const obj = useObjecten()
  const anim = useAnimatie()
  const licht = useLicht()
  const profiel = useProfiel()
  const dpr = useThree((s) => s.viewport.dpr)
  const punten = useRef<THREE.Points>(null)

  const rijen = useMemo(() => (licht.daklichten ? daklichtRijen(ind) : []), [ind, licht.daklichten])

  const geo = useMemo(() => {
    const aantal = PROFIEL_DETAIL[profiel].stof
    const r = prng(ind.cfg.seed + 31337)
    const pos = new Float32Array(aantal * 3)
    const snel = new Float32Array(aantal * 3)
    const inSchacht = rijen.length ? 0.55 : 0

    for (let i = 0; i < aantal; i++) {
      let x: number, y: number, z: number
      if (r() < inSchacht) {
        // Een punt in een schacht: kies een plek in een daklicht en loop langs
        // de zonnestraal naar beneden.
        const rij = rijen[Math.floor(r() * rijen.length)]
        const xd = rij.x0 + r() * (rij.x1 - rij.x0)
        const zd = rij.z + (r() - 0.5) * 1.4
        y = 0.3 + r() * (DAKLICHT_Y - 0.6)
        const t = (DAKLICHT_Y - y) / ZON.y
        x = xd - ZON.x * t
        z = zd - ZON.z * t
      } else {
        x = (r() - 0.5) * HAL.breedte * 1.1
        y = r() * (HAL.hoogte - 0.6) + 0.2
        z = (r() - 0.5) * HAL.diepte * 1.0
      }
      pos[i * 3] = x
      pos[i * 3 + 1] = y
      pos[i * 3 + 2] = z
      snel[i * 3] = 0.3 + r() * 0.8
      snel[i * 3 + 1] = 0.15 + r() * 0.5
      snel[i * 3 + 2] = 0.25 + r() * 0.7
    }

    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    g.setAttribute('snelheid', new THREE.BufferAttribute(snel, 3))
    return g
  }, [ind.cfg.seed, rijen, profiel])

  useEffect(() => () => geo.dispose(), [geo])

  const materiaal = useMemo(
    () => new THREE.ShaderMaterial({
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: {
        tijd: { value: 0 },
        omloop: { value: OMLOOP },
        pixels: { value: 1 },
        zon: { value: ZON.clone() },
        dakY: { value: DAKLICHT_Y },
        rijen: { value: Array.from({ length: MAX_RIJEN }, () => new THREE.Vector3()) },
        aantalRijen: { value: 0 },
        breedte: { value: 1.6 },
        kleurZon: { value: new THREE.Color() },
        kleurHal: { value: new THREE.Color() },
        sterkte: { value: 1 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }),
    [],
  )
  useEffect(() => () => materiaal.dispose(), [materiaal])

  useEffect(() => {
    const u = materiaal.uniforms
    const v = u.rijen.value as THREE.Vector3[]
    rijen.slice(0, MAX_RIJEN).forEach((r, i) => v[i].set(r.z, r.x0, r.x1))
    u.aantalRijen.value = Math.min(MAX_RIJEN, rijen.length)
    u.kleurZon.value.set(licht.zonKleur).multiplyScalar(licht.zonSterkte / 5.5)
    u.kleurHal.value.set(licht.armatuurKleur).multiplyScalar(licht.armatuurSterkte)
  }, [materiaal, rijen, licht.zonKleur, licht.zonSterkte, licht.armatuurKleur, licht.armatuurSterkte])

  // De klok van de hal (`omloop.ts`); met het stof uit in de Studio blijft
  // het hangen waar het hing.
  const tijd = useRef(0)
  useFrame(() => {
    if (anim.aan && anim.stof) tijd.current = halklok.t
    // Binnen de omloop: alles in de shader is daarin rond, en een float32 van
    // drie beursdagen aan seconden is op een halve frame nauwkeurig.
    materiaal.uniforms.tijd.value = tijd.current % OMLOOP
    materiaal.uniforms.pixels.value = dpr
  })

  if (!obj.stof) return null

  return <points ref={punten} geometry={geo} material={materiaal} frustumCulled={false} renderOrder={4} />
}
